import { useMemo } from 'react';
import { ResponsiveBar, type BarDatum } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { formatMetricNumber, truncateChartLabel } from './analyticsHelpers';
import { ChartPanel, MetricSelect } from './analyticsUi';
import type { AssociationRow } from './analyticsTypes';
import type { ChartableColumn } from './chartableColumns';
import { buildColumnChartModel, describeColumnChart } from './columnChartData';
import { getColumnChartTypeOptions, type ColumnChartType } from './columnChartTypes';
import type { MetricDomain } from './metricCatalog';

const BAR_THICKNESS = 26;
const VERTICAL_CHART_HEIGHT = 220;
const DONUT_CHART_HEIGHT = 240;
const HORIZONTAL_TICK_MAX_LENGTH = 18;

const COMPACT_THEME = {
  axis: {
    ticks: {
      text: { fontSize: 11, fill: '#323f57', fontFamily: 'Poppins, sans-serif' },
    },
  },
  labels: {
    text: {
      fontSize: 11,
      fontWeight: 700,
      fill: '#0a104f',
      fontFamily: 'Poppins, sans-serif',
    },
  },
  legends: {
    text: { fontSize: 11, fill: '#323f57', fontFamily: 'Poppins, sans-serif' },
  },
};

interface ColumnChartWidgetProps {
  column: ChartableColumn;
  data: AssociationRow[];
  domain?: MetricDomain;
  chartType?: ColumnChartType;
  countNoun: string;
  onChartTypeChange?: (chartType: ColumnChartType) => void;
}

export function ColumnChartWidget({
  column,
  data,
  domain,
  chartType,
  countNoun,
  onChartTypeChange,
}: ColumnChartWidgetProps) {
  const model = useMemo(
    () => buildColumnChartModel(column, data, { domain, chartType }),
    [chartType, column, data, domain],
  );
  const chartTypeOptions = getColumnChartTypeOptions();
  const isHorizontal = model.chartType === 'horizontalBar';
  const isDonut = model.chartType === 'donut';
  const height = isDonut
    ? DONUT_CHART_HEIGHT
    : isHorizontal
      ? Math.max(150, model.items.length * BAR_THICKNESS + 16)
      : VERTICAL_CHART_HEIGHT;
  const rotateTicks = column.kind === 'numeric' && !isHorizontal;
  const barData = useMemo(
    () => (isHorizontal ? [...model.items].reverse() : model.items) as unknown as BarDatum[],
    [isHorizontal, model.items],
  );
  const pieData = useMemo(
    () =>
      model.items
        .filter(item => item.value > 0)
        .map(item => ({
          id: item.label,
          label: item.label,
          value: item.value,
          color: item.color,
        })),
    [model.items],
  );

  const subtitle = model.chartType === 'donut' ? `Share of ${countNoun}` : `Count of ${countNoun}`;

  return (
    <ChartPanel
      title={column.label}
      subtitle={subtitle}
      className="gda-analytics-panel--compact"
      headerExtra={
        onChartTypeChange ? (
          <MetricSelect
            label="Chart"
            value={model.chartType}
            options={chartTypeOptions}
            onChange={value => onChartTypeChange(value as ColumnChartType)}
          />
        ) : null
      }
    >
      <div
        className="gda-analytics-column-chart"
        style={{ height }}
        role="img"
        aria-label={describeColumnChart(column, model)}
      >
        {isDonut ? (
          <ResponsivePie
            data={pieData}
            margin={{ top: 8, right: 88, bottom: 8, left: 8 }}
            innerRadius={0.58}
            padAngle={1.2}
            cornerRadius={3}
            activeOuterRadiusOffset={6}
            colors={{ datum: 'data.color' }}
            borderWidth={1}
            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
            enableArcLinkLabels={false}
            arcLabelsSkipAngle={16}
            arcLabel={item => (item.value > 0 ? formatMetricNumber(Number(item.value)) : '')}
            theme={COMPACT_THEME}
            tooltip={({ datum }) => (
              <div className="gda-analytics-tooltip">
                <strong>{datum.label}</strong>
                <div>
                  {formatMetricNumber(Number(datum.value))} {countNoun}
                </div>
              </div>
            )}
            legends={[
              {
                anchor: 'right',
                direction: 'column',
                translateX: 78,
                itemWidth: 72,
                itemHeight: 16,
                symbolSize: 8,
                symbolShape: 'circle',
              },
            ]}
          />
        ) : (
          <ResponsiveBar
            data={barData}
            keys={['value']}
            indexBy="label"
            layout={isHorizontal ? 'horizontal' : 'vertical'}
            margin={
              isHorizontal
                ? { top: 4, right: 52, bottom: 4, left: 132 }
                : { top: 22, right: 8, bottom: rotateTicks ? 52 : 32, left: 8 }
            }
            padding={isHorizontal ? 0.22 : 0.18}
            colors={bar => {
              const item = bar.data as unknown as { color?: string };
              return item.color ?? '#0a104f';
            }}
            borderRadius={3}
            theme={COMPACT_THEME}
            enableGridX={false}
            enableGridY={false}
            axisTop={null}
            axisRight={null}
            axisBottom={
              isHorizontal
                ? null
                : {
                    tickSize: 0,
                    tickPadding: 8,
                    tickRotation: rotateTicks ? -35 : 0,
                  }
            }
            axisLeft={
              isHorizontal
                ? {
                    tickSize: 0,
                    tickPadding: 8,
                    format: value => truncateChartLabel(String(value), HORIZONTAL_TICK_MAX_LENGTH),
                  }
                : null
            }
            enableLabel
            labelPosition="end"
            labelOffset={6}
            valueFormat={value => formatMetricNumber(Number(value))}
            labelTextColor="#0a104f"
            tooltip={({ indexValue, value }) => (
              <div className="gda-analytics-tooltip">
                <strong>{indexValue}</strong>
                <div>
                  {formatMetricNumber(Number(value))} {countNoun}
                </div>
              </div>
            )}
          />
        )}
      </div>
    </ChartPanel>
  );
}
